"use client"
// PROTOTYPE — throwaway. Variant A: "SSO-first card". See NOTES.md.
//
// The argument: this is a B2B tool. Almost everyone arriving here has a work
// Google or Microsoft account, so the work-account buttons are the primary path
// and sit above the fold of the card. Email + password stays complete and
// reachable, but demoted to the fallback it actually is.

import React from "react"
import Image from "next/image"
import Link from "next/link"
import { BRAND, BRAND_DARK, GoogleIcon, MicrosoftIcon, Spinner, type SignInViewProps } from "./shell"

export function VariantA({
  email, setEmail, password, setPassword, loading, error, message, onSubmit, onOAuth,
}: SignInViewProps) {
  const field =
    "w-full rounded-lg border border-ui-border bg-white px-3.5 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 outline-none transition-shadow focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20"

  const oauthBtn =
    "w-full flex items-center justify-center gap-3 rounded-lg border border-ui-border bg-white px-4 py-3 text-[14px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]/40"

  return (
    <div
      style={{ ["--brand" as string]: BRAND, ["--brand-dark" as string]: BRAND_DARK }}
      className="min-h-screen flex items-center justify-center bg-canvas px-4 py-10"
    >
      <div className="w-full max-w-[408px]">
        <div className="rounded-2xl border border-ui-border bg-white p-8 shadow-[0_1px_3px_rgba(15,23,42,0.06),0_12px_32px_-12px_rgba(15,23,42,0.12)]">
          <Image
            src="/partnr-wordmark.png"
            alt="Partnr"
            width={800}
            height={280}
            priority
            className="mx-auto h-auto w-[150px]"
          />
          <p className="mt-5 text-center text-[15px] text-slate-500">Sign in to your account</p>

          {message && (
            <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-[13px] text-emerald-800">
              {message}
            </div>
          )}
          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-[13px] text-red-700">
              {error}
            </div>
          )}

          {/* Primary path — work accounts. */}
          <div className="mt-7 space-y-3">
            <button type="button" onClick={() => onOAuth("google")} className={oauthBtn}>
              <GoogleIcon />
              Continue with Google
            </button>
            <button type="button" onClick={() => onOAuth("microsoft")} className={oauthBtn}>
              <MicrosoftIcon />
              Continue with Microsoft
            </button>
          </div>

          <div className="my-7 flex items-center gap-4">
            <span className="h-px flex-1 bg-ui-border" />
            <span className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
              or with email
            </span>
            <span className="h-px flex-1 bg-ui-border" />
          </div>

          {/* Fallback path — email + password. */}
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="a-email" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                Email
              </label>
              <input
                id="a-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={field}
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <label htmlFor="a-password" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                Password
              </label>
              <input
                id="a-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={field}
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand)] px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]/40 focus-visible:ring-offset-2"
            >
              {loading && <Spinner />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-[14px] text-slate-500">
          Don&apos;t have an account?{" "}
          <Link href="/auth/signup" className="font-semibold text-[var(--brand)] hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  )
}
